import { defineMessages, type MessageDescriptor } from 'react-intl';
import type { MailboxProviderId } from 'fym-shared/src/music24/outreach/mailboxProviders';

/*
 * The copy of the mailbox connect guide of each provider (design outreach-v2 o2, o3), for react-intl.
 * The ids are static, so `pnpm trans` finds them. The English text is the text of
 * `MAILBOX_PROVIDERS` (fym-shared `mailboxProviders.ts`); `mailboxGuideCopy.test.ts` checks that the
 * two stay the same. Generated from that file: change the provider file first, then this one.
 */

export interface MailboxGuideStepCopy {
  title: MessageDescriptor;
  body: MessageDescriptor;
  link?: MessageDescriptor;
}

export interface MailboxGuideCopy {
  label: MessageDescriptor;
  tile: MessageDescriptor | null;
  guideTitle: MessageDescriptor;
  guideIntro: MessageDescriptor;
  steps: Record<string, MailboxGuideStepCopy>;
  help: MessageDescriptor[];
}

const gmailMessages = defineMessages({
  label: { id: 'app.sml.outreach.mailboxGuide.gmail.label', defaultMessage: 'Gmail' },
  tile: { id: 'app.sml.outreach.mailboxGuide.gmail.tile', defaultMessage: 'Also Google Workspace' },
  guideTitle: { id: 'app.sml.outreach.mailboxGuide.gmail.guideTitle', defaultMessage: 'Make a Gmail app password' },
  guideIntro: {
    id: 'app.sml.outreach.mailboxGuide.gmail.guideIntro',
    defaultMessage:
      'Google does not let other apps use your normal password. An app password is a separate 16-letter code just for Music24. You can delete it any time in your Google account.',
  },
  twoStepTitle: {
    id: 'app.sml.outreach.mailboxGuide.gmail.twoStep.title',
    defaultMessage: 'Turn on 2-Step Verification',
  },
  twoStepBody: {
    id: 'app.sml.outreach.mailboxGuide.gmail.twoStep.body',
    defaultMessage: 'Google asks for this before it shows app passwords. If it is on already, skip this step.',
  },
  twoStepLink: { id: 'app.sml.outreach.mailboxGuide.gmail.twoStep.link', defaultMessage: 'Open Google security' },
  createTitle: { id: 'app.sml.outreach.mailboxGuide.gmail.create.title', defaultMessage: 'Make the app password' },
  createBody: {
    id: 'app.sml.outreach.mailboxGuide.gmail.create.body',
    defaultMessage:
      'Sign in with the address you pitch from. Type "Music24" as the name and press Create. Google shows a yellow box with 16 letters.',
  },
  createLink: { id: 'app.sml.outreach.mailboxGuide.gmail.create.link', defaultMessage: 'Open App passwords' },
  pasteTitle: {
    id: 'app.sml.outreach.mailboxGuide.gmail.paste.title',
    defaultMessage: 'Copy the 16 letters and paste them here',
  },
  pasteBody: { id: 'app.sml.outreach.mailboxGuide.gmail.paste.body', defaultMessage: 'Spaces are fine.' },
  help1: {
    id: 'app.sml.outreach.mailboxGuide.gmail.help1',
    defaultMessage: '2-Step Verification is still off: do step 1 first.',
  },
  help2: {
    id: 'app.sml.outreach.mailboxGuide.gmail.help2',
    defaultMessage:
      'A work or school account: your admin may have turned app passwords off. Send your admin a message (we write it for you).',
  },
  help3: {
    id: 'app.sml.outreach.mailboxGuide.gmail.help3',
    defaultMessage: 'Advanced Protection is on: Google blocks app passwords. Use another mailbox.',
  },
});

const outlookMessages = defineMessages({
  label: { id: 'app.sml.outreach.mailboxGuide.outlook.label', defaultMessage: 'Outlook' },
  tile: { id: 'app.sml.outreach.mailboxGuide.outlook.tile', defaultMessage: 'Not supported' },
  guideTitle: {
    id: 'app.sml.outreach.mailboxGuide.outlook.guideTitle',
    defaultMessage: 'Outlook does not work with Music24',
  },
  guideIntro: {
    id: 'app.sml.outreach.mailboxGuide.outlook.guideIntro',
    defaultMessage:
      "Microsoft no longer lets apps sign in to Outlook and Microsoft 365 mailboxes with a password. Use another mailbox, for example Gmail or your web host's email.",
  },
  whyTitle: { id: 'app.sml.outreach.mailboxGuide.outlook.why.title', defaultMessage: 'Microsoft keeps apps out' },
  whyBody: {
    id: 'app.sml.outreach.mailboxGuide.outlook.why.body',
    defaultMessage:
      "Microsoft no longer lets apps sign in to Outlook and Microsoft 365 mailboxes with a password. Use another mailbox, for example Gmail or your web host's email.",
  },
  otherTitle: { id: 'app.sml.outreach.mailboxGuide.outlook.other.title', defaultMessage: 'Use another mailbox' },
  otherBody: {
    id: 'app.sml.outreach.mailboxGuide.outlook.other.body',
    defaultMessage: 'A Gmail address or the email of your web host works in a few minutes.',
  },
  otherLink: { id: 'app.sml.outreach.mailboxGuide.outlook.other.link', defaultMessage: 'Use another mailbox' },
  typeTitle: { id: 'app.sml.outreach.mailboxGuide.outlook.type.title', defaultMessage: 'Type the other address' },
  typeBody: {
    id: 'app.sml.outreach.mailboxGuide.outlook.type.body',
    defaultMessage: 'Go back to the first step and type the address curators should answer.',
  },
  help1: {
    id: 'app.sml.outreach.mailboxGuide.outlook.help1',
    defaultMessage:
      "Microsoft no longer lets apps sign in to Outlook and Microsoft 365 mailboxes with a password. Use another mailbox, for example Gmail or your web host's email.",
  },
});

const microsoft365Messages = defineMessages({
  label: { id: 'app.sml.outreach.mailboxGuide.microsoft365.label', defaultMessage: 'Microsoft 365' },
  tile: { id: 'app.sml.outreach.mailboxGuide.microsoft365.tile', defaultMessage: 'Not supported' },
  guideTitle: {
    id: 'app.sml.outreach.mailboxGuide.microsoft365.guideTitle',
    defaultMessage: 'Microsoft 365 does not work with Music24',
  },
  guideIntro: {
    id: 'app.sml.outreach.mailboxGuide.microsoft365.guideIntro',
    defaultMessage:
      "Microsoft no longer lets apps sign in to Outlook and Microsoft 365 mailboxes with a password. Use another mailbox, for example Gmail or your web host's email.",
  },
  whyTitle: { id: 'app.sml.outreach.mailboxGuide.microsoft365.why.title', defaultMessage: 'Microsoft keeps apps out' },
  whyBody: {
    id: 'app.sml.outreach.mailboxGuide.microsoft365.why.body',
    defaultMessage:
      "Microsoft no longer lets apps sign in to Outlook and Microsoft 365 mailboxes with a password. Use another mailbox, for example Gmail or your web host's email.",
  },
  otherTitle: { id: 'app.sml.outreach.mailboxGuide.microsoft365.other.title', defaultMessage: 'Use another mailbox' },
  otherBody: {
    id: 'app.sml.outreach.mailboxGuide.microsoft365.other.body',
    defaultMessage: 'A Gmail address or the email of your web host works in a few minutes.',
  },
  otherLink: { id: 'app.sml.outreach.mailboxGuide.microsoft365.other.link', defaultMessage: 'Use another mailbox' },
  typeTitle: { id: 'app.sml.outreach.mailboxGuide.microsoft365.type.title', defaultMessage: 'Type the other address' },
  typeBody: {
    id: 'app.sml.outreach.mailboxGuide.microsoft365.type.body',
    defaultMessage: 'Go back to the first step and type the address curators should answer.',
  },
  help1: {
    id: 'app.sml.outreach.mailboxGuide.microsoft365.help1',
    defaultMessage:
      "Microsoft no longer lets apps sign in to Outlook and Microsoft 365 mailboxes with a password. Use another mailbox, for example Gmail or your web host's email.",
  },
});

const icloudMessages = defineMessages({
  label: { id: 'app.sml.outreach.mailboxGuide.icloud.label', defaultMessage: 'iCloud Mail' },
  tile: { id: 'app.sml.outreach.mailboxGuide.icloud.tile', defaultMessage: 'icloud.com, me.com' },
  guideTitle: {
    id: 'app.sml.outreach.mailboxGuide.icloud.guideTitle',
    defaultMessage: 'Make an iCloud app-specific password',
  },
  guideIntro: {
    id: 'app.sml.outreach.mailboxGuide.icloud.guideIntro',
    defaultMessage:
      'Apple does not let other apps use your Apple Account password. An app-specific password is a separate code just for Music24. You can delete it any time.',
  },
  twoStepTitle: {
    id: 'app.sml.outreach.mailboxGuide.icloud.twoStep.title',
    defaultMessage: 'Check two-factor authentication',
  },
  twoStepBody: {
    id: 'app.sml.outreach.mailboxGuide.icloud.twoStep.body',
    defaultMessage:
      'Apple shows app-specific passwords only with two-factor authentication. Most Apple Accounts have it on already.',
  },
  twoStepLink: { id: 'app.sml.outreach.mailboxGuide.icloud.twoStep.link', defaultMessage: 'Open Apple Account' },
  createTitle: {
    id: 'app.sml.outreach.mailboxGuide.icloud.create.title',
    defaultMessage: 'Make an app-specific password',
  },
  createBody: {
    id: 'app.sml.outreach.mailboxGuide.icloud.create.body',
    defaultMessage:
      'Go to Sign-In and Security, then App-Specific Passwords. Type "Music24" as the name. Apple shows a password like abcd-efgh-ijkl-mnop.',
  },
  createLink: { id: 'app.sml.outreach.mailboxGuide.icloud.create.link', defaultMessage: 'Open Apple Account' },
  pasteTitle: {
    id: 'app.sml.outreach.mailboxGuide.icloud.paste.title',
    defaultMessage: 'Copy the password and paste it here',
  },
  pasteBody: {
    id: 'app.sml.outreach.mailboxGuide.icloud.paste.body',
    defaultMessage: 'The login is your iCloud address, also when you send from your own domain.',
  },
  help1: {
    id: 'app.sml.outreach.mailboxGuide.icloud.help1',
    defaultMessage: 'The login is the iCloud address (name@icloud.com), even with a custom domain.',
  },
  help2: {
    id: 'app.sml.outreach.mailboxGuide.icloud.help2',
    defaultMessage: 'No App-Specific Passwords section: two-factor authentication is off. Do step 1 first.',
  },
});

const yahooMessages = defineMessages({
  label: { id: 'app.sml.outreach.mailboxGuide.yahoo.label', defaultMessage: 'Yahoo Mail' },
  tile: { id: 'app.sml.outreach.mailboxGuide.yahoo.tile', defaultMessage: 'Also AOL' },
  guideTitle: { id: 'app.sml.outreach.mailboxGuide.yahoo.guideTitle', defaultMessage: 'Make a Yahoo app password' },
  guideIntro: {
    id: 'app.sml.outreach.mailboxGuide.yahoo.guideIntro',
    defaultMessage:
      'Yahoo does not let other apps use your normal password. An app password is a separate code just for Music24. You can delete it any time.',
  },
  securityTitle: {
    id: 'app.sml.outreach.mailboxGuide.yahoo.security.title',
    defaultMessage: 'Open your account security',
  },
  securityBody: {
    id: 'app.sml.outreach.mailboxGuide.yahoo.security.body',
    defaultMessage: 'Sign in with the address you pitch from.',
  },
  securityLink: { id: 'app.sml.outreach.mailboxGuide.yahoo.security.link', defaultMessage: 'Open Yahoo security' },
  createTitle: { id: 'app.sml.outreach.mailboxGuide.yahoo.create.title', defaultMessage: 'Generate an app password' },
  createBody: {
    id: 'app.sml.outreach.mailboxGuide.yahoo.create.body',
    defaultMessage: 'Press "Generate app password", type "Music24" as the name and press Generate password.',
  },
  pasteTitle: {
    id: 'app.sml.outreach.mailboxGuide.yahoo.paste.title',
    defaultMessage: 'Copy the password and paste it here',
  },
  pasteBody: {
    id: 'app.sml.outreach.mailboxGuide.yahoo.paste.body',
    defaultMessage: 'Yahoo shows it only once. Spaces are fine.',
  },
  help1: {
    id: 'app.sml.outreach.mailboxGuide.yahoo.help1',
    defaultMessage: 'No "Generate app password" button: Yahoo asks you to turn on two-step verification first.',
  },
});

const aolMessages = defineMessages({
  label: { id: 'app.sml.outreach.mailboxGuide.aol.label', defaultMessage: 'AOL Mail' },
  tile: { id: 'app.sml.outreach.mailboxGuide.aol.tile', defaultMessage: 'aol.com' },
  guideTitle: { id: 'app.sml.outreach.mailboxGuide.aol.guideTitle', defaultMessage: 'Make an AOL app password' },
  guideIntro: {
    id: 'app.sml.outreach.mailboxGuide.aol.guideIntro',
    defaultMessage:
      'AOL does not let other apps use your normal password. An app password is a separate code just for Music24. You can delete it any time.',
  },
  securityTitle: {
    id: 'app.sml.outreach.mailboxGuide.aol.security.title',
    defaultMessage: 'Open your account security',
  },
  securityBody: {
    id: 'app.sml.outreach.mailboxGuide.aol.security.body',
    defaultMessage: 'Sign in with the address you pitch from.',
  },
  securityLink: { id: 'app.sml.outreach.mailboxGuide.aol.security.link', defaultMessage: 'Open AOL security' },
  createTitle: { id: 'app.sml.outreach.mailboxGuide.aol.create.title', defaultMessage: 'Generate an app password' },
  createBody: {
    id: 'app.sml.outreach.mailboxGuide.aol.create.body',
    defaultMessage: 'Press "Generate app password", type "Music24" as the name and press Generate password.',
  },
  pasteTitle: {
    id: 'app.sml.outreach.mailboxGuide.aol.paste.title',
    defaultMessage: 'Copy the password and paste it here',
  },
  pasteBody: {
    id: 'app.sml.outreach.mailboxGuide.aol.paste.body',
    defaultMessage: 'AOL shows it only once. Spaces are fine.',
  },
  help1: {
    id: 'app.sml.outreach.mailboxGuide.aol.help1',
    defaultMessage: 'No "Generate app password" button: AOL asks you to turn on two-step verification first.',
  },
});

const zohoMessages = defineMessages({
  label: { id: 'app.sml.outreach.mailboxGuide.zoho.label', defaultMessage: 'Zoho Mail' },
  tile: { id: 'app.sml.outreach.mailboxGuide.zoho.tile', defaultMessage: 'Free and paid' },
  guideTitle: { id: 'app.sml.outreach.mailboxGuide.zoho.guideTitle', defaultMessage: 'Make a Zoho app password' },
  guideIntro: {
    id: 'app.sml.outreach.mailboxGuide.zoho.guideIntro',
    defaultMessage: 'Zoho needs IMAP access on and an app password just for Music24. You can delete it any time.',
  },
  imapTitle: { id: 'app.sml.outreach.mailboxGuide.zoho.imap.title', defaultMessage: 'Turn on IMAP access' },
  imapBody: {
    id: 'app.sml.outreach.mailboxGuide.zoho.imap.body',
    defaultMessage: 'In Zoho Mail, open Settings, then Mail accounts, then IMAP, and turn IMAP access on.',
  },
  imapLink: { id: 'app.sml.outreach.mailboxGuide.zoho.imap.link', defaultMessage: 'Open Zoho settings' },
  createTitle: { id: 'app.sml.outreach.mailboxGuide.zoho.create.title', defaultMessage: 'Make an app password' },
  createBody: {
    id: 'app.sml.outreach.mailboxGuide.zoho.create.body',
    defaultMessage: 'Open Security, then App passwords. Press Generate new password and type "Music24" as the name.',
  },
  createLink: { id: 'app.sml.outreach.mailboxGuide.zoho.create.link', defaultMessage: 'Open Zoho security' },
  pasteTitle: {
    id: 'app.sml.outreach.mailboxGuide.zoho.paste.title',
    defaultMessage: 'Copy the password and paste it here',
  },
  pasteBody: { id: 'app.sml.outreach.mailboxGuide.zoho.paste.body', defaultMessage: 'Zoho shows it only once.' },
  help1: {
    id: 'app.sml.outreach.mailboxGuide.zoho.help1',
    defaultMessage: 'Zoho answered "You are yet to enable IMAP": do step 1 first.',
  },
  help2: {
    id: 'app.sml.outreach.mailboxGuide.zoho.help2',
    defaultMessage: 'An EU account (zoho.eu): we use the zoho.eu servers for you.',
  },
});

const fastmailMessages = defineMessages({
  label: { id: 'app.sml.outreach.mailboxGuide.fastmail.label', defaultMessage: 'Fastmail' },
  guideTitle: {
    id: 'app.sml.outreach.mailboxGuide.fastmail.guideTitle',
    defaultMessage: 'Make a Fastmail app password',
  },
  guideIntro: {
    id: 'app.sml.outreach.mailboxGuide.fastmail.guideIntro',
    defaultMessage:
      'Fastmail does not let other apps use your normal password. An app password is a separate code just for Music24.',
  },
  settingsTitle: {
    id: 'app.sml.outreach.mailboxGuide.fastmail.settings.title',
    defaultMessage: 'Open your app passwords',
  },
  settingsBody: {
    id: 'app.sml.outreach.mailboxGuide.fastmail.settings.body',
    defaultMessage: 'Go to Settings, then Privacy & Security, then Manage app passwords and access.',
  },
  settingsLink: {
    id: 'app.sml.outreach.mailboxGuide.fastmail.settings.link',
    defaultMessage: 'Open Fastmail settings',
  },
  createTitle: { id: 'app.sml.outreach.mailboxGuide.fastmail.create.title', defaultMessage: 'Make a new app password' },
  createBody: {
    id: 'app.sml.outreach.mailboxGuide.fastmail.create.body',
    defaultMessage: 'Press New app password. Choose the access "Mail (IMAP/POP/SMTP)" and type "Music24" as the name.',
  },
  pasteTitle: {
    id: 'app.sml.outreach.mailboxGuide.fastmail.paste.title',
    defaultMessage: 'Copy the password and paste it here',
  },
  pasteBody: {
    id: 'app.sml.outreach.mailboxGuide.fastmail.paste.body',
    defaultMessage: 'Fastmail shows it only once. Spaces are fine.',
  },
  help1: {
    id: 'app.sml.outreach.mailboxGuide.fastmail.help1',
    defaultMessage: 'Choose "Mail (IMAP/POP/SMTP)" as the access. A password for other access does not work.',
  },
});

const godaddyMessages = defineMessages({
  label: { id: 'app.sml.outreach.mailboxGuide.godaddy.label', defaultMessage: 'GoDaddy' },
  tile: { id: 'app.sml.outreach.mailboxGuide.godaddy.tile', defaultMessage: 'Workspace Email' },
  guideTitle: { id: 'app.sml.outreach.mailboxGuide.godaddy.guideTitle', defaultMessage: 'Your email password' },
  guideIntro: {
    id: 'app.sml.outreach.mailboxGuide.godaddy.guideIntro',
    defaultMessage: 'For a mailbox at GoDaddy, the normal email password works.',
  },
  passwordTitle: {
    id: 'app.sml.outreach.mailboxGuide.godaddy.password.title',
    defaultMessage: 'Find your email password',
  },
  passwordBody: {
    id: 'app.sml.outreach.mailboxGuide.godaddy.password.body',
    defaultMessage:
      'It is the normal password of this email: the one you use to read it in the webmail of GoDaddy. No app password is needed.',
  },
  passwordLink: { id: 'app.sml.outreach.mailboxGuide.godaddy.password.link', defaultMessage: 'Open GoDaddy webmail' },
  serversTitle: { id: 'app.sml.outreach.mailboxGuide.godaddy.servers.title', defaultMessage: 'We fill in the servers' },
  serversBody: {
    id: 'app.sml.outreach.mailboxGuide.godaddy.servers.body',
    defaultMessage:
      'We know the mail servers of GoDaddy, so there is nothing to type here. If your email at GoDaddy runs on Microsoft 365, it does not work with Music24.',
  },
  pasteTitle: { id: 'app.sml.outreach.mailboxGuide.godaddy.paste.title', defaultMessage: 'Type the password here' },
  pasteBody: {
    id: 'app.sml.outreach.mailboxGuide.godaddy.paste.body',
    defaultMessage: 'We check it right away. We send nothing and read nothing during the check.',
  },
  help1: {
    id: 'app.sml.outreach.mailboxGuide.godaddy.help1',
    defaultMessage: 'GoDaddy email that runs on Microsoft 365 does not work with Music24. Use another mailbox.',
  },
});

const ionosMessages = defineMessages({
  label: { id: 'app.sml.outreach.mailboxGuide.ionos.label', defaultMessage: 'IONOS' },
  tile: { id: 'app.sml.outreach.mailboxGuide.ionos.tile', defaultMessage: 'Mail Basic, Mail Business' },
  guideTitle: { id: 'app.sml.outreach.mailboxGuide.ionos.guideTitle', defaultMessage: 'Your email password' },
  guideIntro: {
    id: 'app.sml.outreach.mailboxGuide.ionos.guideIntro',
    defaultMessage: 'For a mailbox at IONOS, the normal email password works.',
  },
  passwordTitle: {
    id: 'app.sml.outreach.mailboxGuide.ionos.password.title',
    defaultMessage: 'Find your email password',
  },
  passwordBody: {
    id: 'app.sml.outreach.mailboxGuide.ionos.password.body',
    defaultMessage:
      'It is the normal password of this email: the one you use to read it in the webmail of IONOS. No app password is needed.',
  },
  passwordLink: { id: 'app.sml.outreach.mailboxGuide.ionos.password.link', defaultMessage: 'Open IONOS webmail' },
  serversTitle: { id: 'app.sml.outreach.mailboxGuide.ionos.servers.title', defaultMessage: 'We fill in the servers' },
  serversBody: {
    id: 'app.sml.outreach.mailboxGuide.ionos.servers.body',
    defaultMessage:
      'We know the mail servers of IONOS, so there is nothing to type here. If your email at IONOS runs on Microsoft 365, it does not work with Music24.',
  },
  pasteTitle: { id: 'app.sml.outreach.mailboxGuide.ionos.paste.title', defaultMessage: 'Type the password here' },
  pasteBody: {
    id: 'app.sml.outreach.mailboxGuide.ionos.paste.body',
    defaultMessage: 'We check it right away. We send nothing and read nothing during the check.',
  },
  help1: {
    id: 'app.sml.outreach.mailboxGuide.ionos.help1',
    defaultMessage: 'You forgot the password: set a new one for this mailbox in the IONOS Email section.',
  },
});

const hostingerMessages = defineMessages({
  label: { id: 'app.sml.outreach.mailboxGuide.hostinger.label', defaultMessage: 'Hostinger' },
  tile: { id: 'app.sml.outreach.mailboxGuide.hostinger.tile', defaultMessage: 'Hostinger Email' },
  guideTitle: { id: 'app.sml.outreach.mailboxGuide.hostinger.guideTitle', defaultMessage: 'Your email password' },
  guideIntro: {
    id: 'app.sml.outreach.mailboxGuide.hostinger.guideIntro',
    defaultMessage: 'For a mailbox at Hostinger, the normal email password works.',
  },
  passwordTitle: {
    id: 'app.sml.outreach.mailboxGuide.hostinger.password.title',
    defaultMessage: 'Find your email password',
  },
  passwordBody: {
    id: 'app.sml.outreach.mailboxGuide.hostinger.password.body',
    defaultMessage:
      'It is the normal password of this email: the one you use to read it in the webmail of Hostinger. No app password is needed.',
  },
  passwordLink: {
    id: 'app.sml.outreach.mailboxGuide.hostinger.password.link',
    defaultMessage: 'Open Hostinger webmail',
  },
  serversTitle: {
    id: 'app.sml.outreach.mailboxGuide.hostinger.servers.title',
    defaultMessage: 'We fill in the servers',
  },
  serversBody: {
    id: 'app.sml.outreach.mailboxGuide.hostinger.servers.body',
    defaultMessage:
      'We know the mail servers of Hostinger, so there is nothing to type here. If your email at Hostinger runs on Microsoft 365, it does not work with Music24.',
  },
  pasteTitle: { id: 'app.sml.outreach.mailboxGuide.hostinger.paste.title', defaultMessage: 'Type the password here' },
  pasteBody: {
    id: 'app.sml.outreach.mailboxGuide.hostinger.paste.body',
    defaultMessage: 'We check it right away. We send nothing and read nothing during the check.',
  },
  help1: {
    id: 'app.sml.outreach.mailboxGuide.hostinger.help1',
    defaultMessage: 'You forgot the password: set a new one for this mailbox in hPanel, under Emails.',
  },
});

const protonMessages = defineMessages({
  label: { id: 'app.sml.outreach.mailboxGuide.proton.label', defaultMessage: 'Proton Mail' },
  tile: { id: 'app.sml.outreach.mailboxGuide.proton.tile', defaultMessage: 'Not supported' },
  guideTitle: {
    id: 'app.sml.outreach.mailboxGuide.proton.guideTitle',
    defaultMessage: 'Proton Mail does not work with Music24',
  },
  guideIntro: {
    id: 'app.sml.outreach.mailboxGuide.proton.guideIntro',
    defaultMessage:
      'Proton lets other apps in only through the Bridge app on a computer that is always on. Music24 cannot use it.',
  },
  whyTitle: { id: 'app.sml.outreach.mailboxGuide.proton.why.title', defaultMessage: 'Proton keeps other apps out' },
  whyBody: {
    id: 'app.sml.outreach.mailboxGuide.proton.why.body',
    defaultMessage: 'Your mail is safe at Proton, but Music24 cannot sign in to send your pitches.',
  },
  otherTitle: { id: 'app.sml.outreach.mailboxGuide.proton.other.title', defaultMessage: 'Use another mailbox' },
  otherBody: {
    id: 'app.sml.outreach.mailboxGuide.proton.other.body',
    defaultMessage: 'A Gmail or iCloud address works in a few minutes.',
  },
  otherLink: { id: 'app.sml.outreach.mailboxGuide.proton.other.link', defaultMessage: 'Use another mailbox' },
  typeTitle: { id: 'app.sml.outreach.mailboxGuide.proton.type.title', defaultMessage: 'Type the other address' },
  typeBody: {
    id: 'app.sml.outreach.mailboxGuide.proton.type.body',
    defaultMessage: 'Go back to the first step and type the address curators should answer.',
  },
});

const otherMessages = defineMessages({
  label: { id: 'app.sml.outreach.mailboxGuide.other.label', defaultMessage: 'Something else' },
  tile: { id: 'app.sml.outreach.mailboxGuide.other.tile', defaultMessage: 'GoDaddy, IONOS, Hostinger, your web host' },
  guideTitle: { id: 'app.sml.outreach.mailboxGuide.other.guideTitle', defaultMessage: 'Your email password' },
  guideIntro: {
    id: 'app.sml.outreach.mailboxGuide.other.guideIntro',
    defaultMessage:
      'For a mailbox at your web host, the normal email password works. It is the password you use to read this email in the browser of your host.',
  },
  passwordTitle: {
    id: 'app.sml.outreach.mailboxGuide.other.password.title',
    defaultMessage: 'Find your email password',
  },
  passwordBody: {
    id: 'app.sml.outreach.mailboxGuide.other.password.body',
    defaultMessage: 'It is the password you use to read this email in the browser of your host.',
  },
  serversTitle: { id: 'app.sml.outreach.mailboxGuide.other.servers.title', defaultMessage: 'Find your servers' },
  serversBody: {
    id: 'app.sml.outreach.mailboxGuide.other.servers.body',
    defaultMessage:
      'Your host lists them on a help page called "email settings", "IMAP settings" or "mail client setup". You need two names that look like imap.yourhost.com and smtp.yourhost.com.',
  },
  serversLink: {
    id: 'app.sml.outreach.mailboxGuide.other.servers.link',
    defaultMessage: 'Where do I find my servers?',
  },
  typeTitle: {
    id: 'app.sml.outreach.mailboxGuide.other.type.title',
    defaultMessage: 'Type the servers and the password',
  },
  typeBody: {
    id: 'app.sml.outreach.mailboxGuide.other.type.body',
    defaultMessage: 'Not sure about the port? Leave it: we try 993 and 465, then 587.',
  },
});

/** The copy of every provider, by id. */
export const MAILBOX_GUIDE_COPY: Record<MailboxProviderId, MailboxGuideCopy> = {
  gmail: {
    label: gmailMessages.label,
    tile: gmailMessages.tile,
    guideTitle: gmailMessages.guideTitle,
    guideIntro: gmailMessages.guideIntro,
    steps: {
      twoStep: { title: gmailMessages.twoStepTitle, body: gmailMessages.twoStepBody, link: gmailMessages.twoStepLink },
      create: { title: gmailMessages.createTitle, body: gmailMessages.createBody, link: gmailMessages.createLink },
      paste: { title: gmailMessages.pasteTitle, body: gmailMessages.pasteBody },
    },
    help: [gmailMessages.help1, gmailMessages.help2, gmailMessages.help3],
  },
  outlook: {
    label: outlookMessages.label,
    tile: outlookMessages.tile,
    guideTitle: outlookMessages.guideTitle,
    guideIntro: outlookMessages.guideIntro,
    steps: {
      why: { title: outlookMessages.whyTitle, body: outlookMessages.whyBody },
      other: { title: outlookMessages.otherTitle, body: outlookMessages.otherBody, link: outlookMessages.otherLink },
      type: { title: outlookMessages.typeTitle, body: outlookMessages.typeBody },
    },
    help: [outlookMessages.help1],
  },
  microsoft365: {
    label: microsoft365Messages.label,
    tile: microsoft365Messages.tile,
    guideTitle: microsoft365Messages.guideTitle,
    guideIntro: microsoft365Messages.guideIntro,
    steps: {
      why: { title: microsoft365Messages.whyTitle, body: microsoft365Messages.whyBody },
      other: {
        title: microsoft365Messages.otherTitle,
        body: microsoft365Messages.otherBody,
        link: microsoft365Messages.otherLink,
      },
      type: { title: microsoft365Messages.typeTitle, body: microsoft365Messages.typeBody },
    },
    help: [microsoft365Messages.help1],
  },
  icloud: {
    label: icloudMessages.label,
    tile: icloudMessages.tile,
    guideTitle: icloudMessages.guideTitle,
    guideIntro: icloudMessages.guideIntro,
    steps: {
      twoStep: {
        title: icloudMessages.twoStepTitle,
        body: icloudMessages.twoStepBody,
        link: icloudMessages.twoStepLink,
      },
      create: { title: icloudMessages.createTitle, body: icloudMessages.createBody, link: icloudMessages.createLink },
      paste: { title: icloudMessages.pasteTitle, body: icloudMessages.pasteBody },
    },
    help: [icloudMessages.help1, icloudMessages.help2],
  },
  yahoo: {
    label: yahooMessages.label,
    tile: yahooMessages.tile,
    guideTitle: yahooMessages.guideTitle,
    guideIntro: yahooMessages.guideIntro,
    steps: {
      security: {
        title: yahooMessages.securityTitle,
        body: yahooMessages.securityBody,
        link: yahooMessages.securityLink,
      },
      create: { title: yahooMessages.createTitle, body: yahooMessages.createBody },
      paste: { title: yahooMessages.pasteTitle, body: yahooMessages.pasteBody },
    },
    help: [yahooMessages.help1],
  },
  aol: {
    label: aolMessages.label,
    tile: aolMessages.tile,
    guideTitle: aolMessages.guideTitle,
    guideIntro: aolMessages.guideIntro,
    steps: {
      security: { title: aolMessages.securityTitle, body: aolMessages.securityBody, link: aolMessages.securityLink },
      create: { title: aolMessages.createTitle, body: aolMessages.createBody },
      paste: { title: aolMessages.pasteTitle, body: aolMessages.pasteBody },
    },
    help: [aolMessages.help1],
  },
  zoho: {
    label: zohoMessages.label,
    tile: zohoMessages.tile,
    guideTitle: zohoMessages.guideTitle,
    guideIntro: zohoMessages.guideIntro,
    steps: {
      imap: { title: zohoMessages.imapTitle, body: zohoMessages.imapBody, link: zohoMessages.imapLink },
      create: { title: zohoMessages.createTitle, body: zohoMessages.createBody, link: zohoMessages.createLink },
      paste: { title: zohoMessages.pasteTitle, body: zohoMessages.pasteBody },
    },
    help: [zohoMessages.help1, zohoMessages.help2],
  },
  fastmail: {
    label: fastmailMessages.label,
    tile: null,
    guideTitle: fastmailMessages.guideTitle,
    guideIntro: fastmailMessages.guideIntro,
    steps: {
      settings: {
        title: fastmailMessages.settingsTitle,
        body: fastmailMessages.settingsBody,
        link: fastmailMessages.settingsLink,
      },
      create: { title: fastmailMessages.createTitle, body: fastmailMessages.createBody },
      paste: { title: fastmailMessages.pasteTitle, body: fastmailMessages.pasteBody },
    },
    help: [fastmailMessages.help1],
  },
  godaddy: {
    label: godaddyMessages.label,
    tile: godaddyMessages.tile,
    guideTitle: godaddyMessages.guideTitle,
    guideIntro: godaddyMessages.guideIntro,
    steps: {
      password: {
        title: godaddyMessages.passwordTitle,
        body: godaddyMessages.passwordBody,
        link: godaddyMessages.passwordLink,
      },
      servers: { title: godaddyMessages.serversTitle, body: godaddyMessages.serversBody },
      paste: { title: godaddyMessages.pasteTitle, body: godaddyMessages.pasteBody },
    },
    help: [godaddyMessages.help1],
  },
  ionos: {
    label: ionosMessages.label,
    tile: ionosMessages.tile,
    guideTitle: ionosMessages.guideTitle,
    guideIntro: ionosMessages.guideIntro,
    steps: {
      password: {
        title: ionosMessages.passwordTitle,
        body: ionosMessages.passwordBody,
        link: ionosMessages.passwordLink,
      },
      servers: { title: ionosMessages.serversTitle, body: ionosMessages.serversBody },
      paste: { title: ionosMessages.pasteTitle, body: ionosMessages.pasteBody },
    },
    help: [ionosMessages.help1],
  },
  hostinger: {
    label: hostingerMessages.label,
    tile: hostingerMessages.tile,
    guideTitle: hostingerMessages.guideTitle,
    guideIntro: hostingerMessages.guideIntro,
    steps: {
      password: {
        title: hostingerMessages.passwordTitle,
        body: hostingerMessages.passwordBody,
        link: hostingerMessages.passwordLink,
      },
      servers: { title: hostingerMessages.serversTitle, body: hostingerMessages.serversBody },
      paste: { title: hostingerMessages.pasteTitle, body: hostingerMessages.pasteBody },
    },
    help: [hostingerMessages.help1],
  },
  proton: {
    label: protonMessages.label,
    tile: protonMessages.tile,
    guideTitle: protonMessages.guideTitle,
    guideIntro: protonMessages.guideIntro,
    steps: {
      why: { title: protonMessages.whyTitle, body: protonMessages.whyBody },
      other: { title: protonMessages.otherTitle, body: protonMessages.otherBody, link: protonMessages.otherLink },
      type: { title: protonMessages.typeTitle, body: protonMessages.typeBody },
    },
    help: [],
  },
  other: {
    label: otherMessages.label,
    tile: otherMessages.tile,
    guideTitle: otherMessages.guideTitle,
    guideIntro: otherMessages.guideIntro,
    steps: {
      password: { title: otherMessages.passwordTitle, body: otherMessages.passwordBody },
      servers: { title: otherMessages.serversTitle, body: otherMessages.serversBody, link: otherMessages.serversLink },
      type: { title: otherMessages.typeTitle, body: otherMessages.typeBody },
    },
    help: [],
  },
};
